import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import Layout from 'components/common/layout/Layout';
import SearchFilter from 'components/common/forms/SearchFilter';
import StudentSearchFilter from 'components/pages/professor/student/SearchFilter';
import Paging from 'components/common/nav/Paging';
import TableForm from 'components/common/lists/TableForm';
import { ROUTE_PATH } from 'constants/routePath';
import { DEFAULT_PAGE_SIZE } from 'constants/page';
import { TTableColumn } from 'types/components/table';
import { RegistrationStatusType } from 'types/form/mypage';
import { SortDirection } from 'types/apis/common';
import {
  TStudentListParams,
  TStudentTableInfo,
  StudentListSortBy,
  StudentListSortByFormLabel,
} from 'types/apis/professor';
import { getStudentList } from 'apis/professor';

const tableColumns: TTableColumn[] = [
  {
    name: 'no',
    label: '번호',
    width: '70px',
  },
  {
    name: 'name',
    label: '이름',
    width: '100px',
    onClick: () => {
      return;
    },
  },
  {
    name: 'loginId',
    label: '학번',
    width: '120px',
    onClick: () => {
      return;
    },
  },
  {
    name: 'schoolYear',
    label: '학년',
    width: '90px',
    onClick: () => {
      return;
    },
  },
  { name: 'phoneNum', label: '연락처', width: '160px' },
  { name: 'courseName', label: '강의', width: '240px' },
  { name: 'studClass', label: '분반', width: '100px' },
  {
    name: 'score',
    label: '최근회차평가',
    width: '140px',
    onClick: () => {
      return;
    },
  },
];

const Student = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const [page, setPage] = useState(Number(query.get('page')) || 1);
  const [params, setParams] = useState({
    page,
    size: DEFAULT_PAGE_SIZE,
    courseInfoId: Number(query.get('courseInfoId')) || undefined,
    registrationStatus:
      (query.get('registrationStatus') as RegistrationStatusType) || undefined,
    searchWord: query.get('searchWord') || undefined,
    schoolYear: query.getAll('schoolYear').map(Number) || undefined,
    score: query.getAll('score') || undefined,
    sortBy: query.get('sortBy') || undefined,
    isAsc:
      query.get('sort') === SortDirection.Asc
        ? true
        : query.get('sort') === SortDirection.Desc
        ? false
        : undefined,
  });
  const [sortBy, setSortBy] = useState(query.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(query.get('sort') || '');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (sortBy !== query.get('sortBy')) setSortBy('');
    if (sortDirection !== query.get('sort')) setSortDirection('');
    setParams({
      ...params,
      page,
      courseInfoId: Number(query.get('courseInfoId')) || undefined,
      registrationStatus:
        (query.get('registrationStatus') as RegistrationStatusType) ||
        undefined,
      searchWord: query.get('searchWord') || undefined,
      schoolYear: query.getAll('schoolYear').map(Number) || undefined,
      score: query.getAll('score') || undefined,
    });
  }, [location.search, page]);

  const { data: getStudentListData, isFetching } = useQuery(
    ['getStudentList', params],
    () => getStudentList(params as TStudentListParams),
    { enabled: !!params.courseInfoId && !!params.registrationStatus }
  );

  const tableData: TStudentTableInfo[] =
    getStudentListData?.data?.studentList || [];
  const tablePaging = getStudentListData?.data?.paging;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onChangeFilter = (
    schoolYear: number[] | undefined,
    score: string[] | undefined
  ) => {
    query.delete('schoolYear');
    query.delete('score');
    if (schoolYear) {
      schoolYear.map((year) => query.append('schoolYear', year.toString()));
    }
    if (score) {
      score.map((score) => query.append('score', score));
    }
    navigate({
      search: query.toString(),
    });
  };

  const onClickSort = (label: string) => {
    const sortAsc =
      sortDirection === ''
        ? SortDirection.Asc
        : sortDirection === SortDirection.Asc
        ? SortDirection.Desc
        : '';
    const sortBy = {
      [StudentListSortByFormLabel.Name]: StudentListSortBy.Name,
      [StudentListSortByFormLabel.LoginId]: StudentListSortBy.LoginId,
      [StudentListSortByFormLabel.SchoolYear]: StudentListSortBy.SchoolYear,
      [StudentListSortByFormLabel.Score]: StudentListSortBy.Score,
    }[label as StudentListSortByFormLabel];

    setSortDirection(sortAsc);
    setSortBy(sortBy);

    if (page !== 1) {
      setPage(1);
    }
    if (!sortAsc) {
      query.delete('sort');
      query.delete('sortBy');
    } else {
      query.set('sort', sortAsc);
      query.set('sortBy', sortBy);
    }
    navigate({
      search: query.toString(),
    });

    setParams({
      ...params,
      page: 1,
      sortBy: (query.get('sortBy') as StudentListSortBy) || undefined,
      isAsc:
        sortAsc === SortDirection.Asc
          ? true
          : sortAsc === SortDirection.Desc
          ? false
          : undefined,
    });
  };

  return (
    <Layout>
      <h1 className="main_title mt40 mb24">학생</h1>

      <SearchFilter
        dropdown={[
          {
            id: 'course',
            placeholder: '강의명',
            width: '240px',
          },
          {
            id: 'courseInfo',
            placeholder: '분반',
            width: '160px',
          },
          {
            id: 'registrationStatus',
            placeholder: '재적 상태',
            width: '160px',
          },
        ]}
        input={{
          placeholder: '이름, 학번, 연락처를 입력해 주세요.',
          width: '318px',
        }}
        onChange={() => setPage(1)}
      />

      <StudentSearchFilter
        schoolYears={(params.schoolYear as unknown as string[])?.map(Number)}
        scores={params.score as unknown as string[]}
        onChange={onChangeFilter}
      />

      <TableForm
        data={tableData}
        columns={tableColumns}
        paging={tablePaging}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onClickSort={onClickSort}
        onClickRowById={(id) =>
          navigate(
            `${ROUTE_PATH.professor.STUDENT}/${id}?courseInfoId=${params.courseInfoId}`,
            {
              state: {
                from: {
                  pathname: location.pathname,
                  search: location.search,
                },
              },
            }
          )
        }
        idField="userId"
        emptyMessage="제출된 명단이 없습니다."
        isLoading={isLoading}
      />

      {tablePaging && tablePaging.totalElements > 0 && (
        <div className="mt32">
          <Paging
            activePage={page}
            totalItemsCount={tablePaging.totalElements}
            handlePageChange={setPage}
          />
        </div>
      )}
    </Layout>
  );
};

export default Student;
